import { Button, ButtonProps } from 'v5/design-system/Button';
import { LoadingSpinner } from 'v5/design-system/LoadingSpinner';
import InlineToast from 'v5/patterns/widgets/InlineToast';
import { MutationData, RemoteData } from '@passionware/monads';
import { CheckCircle2, ChevronDown, CircleAlert } from 'lucide-react';
import { ReactNode, useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from 'v5/platform/dom/cn';

export type AsyncButtonProps = ButtonProps & {
  /**
   * If passed, the button will visualize disabled state and add progress spinner
   */
  mutation?: MutationData<unknown, unknown>;
  remoteData?: RemoteData<unknown>;
  leftIcon?: ReactNode;
  doneIconDuration?: number;
} & (
    | { rightIcon?: ReactNode; buttonType?: 'normal' }
    | {
        buttonType: 'more';
        rightIcon?: never;
      }
  );

export type AsyncIconButtonProps = Omit<ButtonProps, 'children'> & {
  /**
   * If passed, the button will visualize disabled state and add progress spinner
   */
  mutation?: MutationData<unknown, unknown>;
  remoteData?: RemoteData<unknown>;
  icon?: ReactNode;
  doneIconDuration?: number;
};

export function AsyncIconButton({
  icon,
  mutation,
  remoteData,
  visuallyDisabled,
  onClickEvenWhenDisabled,
  doneIconDuration = 1000,
  layout = 'icon',
  ...rest
}: AsyncIconButtonProps) {
  const status = mutation?.status ?? remoteData?.status ?? 'idle';
  const wasInitiallyDone = useRef(status === 'success');

  const isInProgress = status === 'pending';

  const isLoading = isInProgress;
  const isError = status === 'error';
  const isSuccess =
    mutation && status === 'success' && !wasInitiallyDone.current;

  // Determine which content to show
  const getContent = () => {
    if (isLoading) return { type: 'loading', key: 'loading' };
    if (isError) return { type: 'error', key: 'error' };
    if (isSuccess) return { type: 'success', key: 'success' };
    if (icon) return { type: 'icon', key: 'icon' };
    return null;
  };

  const content = getContent();

  return (
    <Button
      {...rest}
      layout={layout}
      onClickEvenWhenDisabled={
        isInProgress ? undefined : onClickEvenWhenDisabled
      }
      visuallyDisabled={isInProgress || visuallyDisabled}
      aria-busy={isInProgress ?? rest['aria-busy']}
      className={cn(rest.className, isInProgress && 'cursor-wait')}
    >
      <AnimatePresence mode="wait" initial={false}>
        {content && (
          <motion.div
            key={content.key}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className="flex items-center justify-center"
          >
            {content.type === 'loading' && <LoadingSpinner />}
            {content.type === 'error' && <CircleAlert />}
            {content.type === 'success' && (
              <InlineToast delay={doneIconDuration} contentAfter={icon}>
                <CheckCircle2 />
              </InlineToast>
            )}
            {content.type === 'icon' && icon}
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  );
}

/**
 * Centralized usage patterns for buttons with async state management
 */
export function AsyncButton({
  children,
  mutation,
  remoteData,
  leftIcon,
  rightIcon,
  buttonType,
  visuallyDisabled,
  onClickEvenWhenDisabled,
  doneIconDuration = 1000,
  className,
  ...rest
}: AsyncButtonProps) {
  const status = mutation?.status ?? remoteData?.status ?? 'idle';
  const wasInitiallyDone = useRef(status === 'success');

  const isInProgress = status === 'pending';

  const iconForLeft = leftIcon;
  const iconForRight =
    buttonType === 'more' ? <ChevronDown className="size-5" /> : rightIcon;

  const loadingSlot = !iconForLeft && iconForRight ? 'right' : 'left';
  const isLoadingButton = isInProgress;
  const isErrorButton = status === 'error';
  const isSuccessButton =
    mutation && status === 'success' && !wasInitiallyDone.current;

  // Track when success icon should be visible (disappears after doneIconDuration)
  const [showSuccessIcon, setShowSuccessIcon] = useState(false);

  useEffect(() => {
    if (isSuccessButton && !isLoadingButton) {
      setShowSuccessIcon(true);
      const timer = setTimeout(() => {
        setShowSuccessIcon(false);
      }, doneIconDuration);
      return () => clearTimeout(timer);
    } else if (!isSuccessButton) {
      setShowSuccessIcon(false);
    }
  }, [isSuccessButton, isLoadingButton, doneIconDuration]);

  // Track if we should show the icon slot - only when there's actual content to display
  const shouldShowLeftSlot =
    isLoadingButton ||
    (isErrorButton && !isLoadingButton) ||
    (showSuccessIcon && !isLoadingButton);
  const shouldShowRightSlot =
    isLoadingButton ||
    (isErrorButton && !isLoadingButton) ||
    (showSuccessIcon && !isLoadingButton);

  const size = rest.size ?? 'md';
  // Icon margin based on button size
  const iconMargin = size === 'xs' ? 6 : size === 'sm' ? 8 : 10;
  const iconMarginClass =
    size === 'xs' ? 'mr-1.5' : size === 'sm' ? 'mr-2' : 'mr-2.5';
  const iconMarginClassLeft =
    size === 'xs' ? 'ml-1.5' : size === 'sm' ? 'ml-2' : 'ml-2.5';

  return (
    <Button
      {...rest}
      onClickEvenWhenDisabled={
        isInProgress ? undefined : onClickEvenWhenDisabled
      }
      visuallyDisabled={isInProgress || visuallyDisabled}
      aria-busy={isInProgress ?? rest['aria-busy']}
      className={cn('gap-0', className)}
    >
      {/* Left slot */}
      {loadingSlot === 'left' ? (
        !iconForLeft ? (
          <AnimatePresence mode="wait">
            {shouldShowLeftSlot && (
              <motion.div
                key="icon-slot-left"
                initial={{ width: 0, opacity: 0, marginRight: 0 }}
                animate={{ width: 'auto', opacity: 1, marginRight: iconMargin }}
                exit={{ width: 0, opacity: 0, marginRight: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                {isLoadingButton && (
                  <motion.div
                    key="loading-left"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center"
                  >
                    <LoadingSpinner />
                  </motion.div>
                )}
                {isErrorButton && !isLoadingButton && (
                  <motion.div
                    key="error-left"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center"
                  >
                    <CircleAlert />
                  </motion.div>
                )}
                {isSuccessButton && !isLoadingButton && (
                  <motion.div
                    key="success-left"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center"
                  >
                    <InlineToast delay={doneIconDuration}>
                      <CheckCircle2 />
                    </InlineToast>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        ) : (
          <div className={cn('relative', iconMarginClass)}>
            <div className="invisible" aria-hidden="true">
              {iconForLeft}
            </div>
            {isLoadingButton && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className="absolute left-0 top-0"
              >
                <LoadingSpinner />
              </motion.div>
            )}
            {isErrorButton && !isLoadingButton && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className="absolute left-0 top-0"
              >
                <CircleAlert />
              </motion.div>
            )}
            {isSuccessButton && !isLoadingButton && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className="absolute left-0 top-0"
              >
                <InlineToast
                  delay={doneIconDuration}
                  contentAfter={
                    <motion.div
                      initial={{ opacity: 1 }}
                      animate={{
                        opacity: isLoadingButton || isErrorButton ? 0 : 1
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      {iconForLeft}
                    </motion.div>
                  }
                >
                  <CheckCircle2 />
                </InlineToast>
              </motion.div>
            )}
            {!isSuccessButton && (
              <motion.div
                initial={{ opacity: 1 }}
                animate={{
                  opacity:
                    isLoadingButton || isErrorButton || isSuccessButton ? 0 : 1
                }}
                transition={{ duration: 0.2 }}
                className="absolute left-0 top-0"
              >
                {iconForLeft}
              </motion.div>
            )}
          </div>
        )
      ) : (
        iconForLeft && <span className={iconMarginClass}>{iconForLeft}</span>
      )}

      <div>{children}</div>

      {/* Right slot */}
      {loadingSlot === 'right' ? (
        !iconForRight ? (
          <AnimatePresence mode="wait">
            {shouldShowRightSlot && (
              <motion.div
                key="icon-slot-right"
                initial={{ width: 0, opacity: 0, marginLeft: 0 }}
                animate={{ width: 'auto', opacity: 1, marginLeft: iconMargin }}
                exit={{ width: 0, opacity: 0, marginLeft: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                {isLoadingButton && (
                  <motion.div
                    key="loading-right"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center"
                  >
                    <LoadingSpinner />
                  </motion.div>
                )}
                {isErrorButton && !isLoadingButton && (
                  <motion.div
                    key="error-right"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center"
                  >
                    <CircleAlert />
                  </motion.div>
                )}
                {isSuccessButton && !isLoadingButton && (
                  <motion.div
                    key="success-right"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center"
                  >
                    <InlineToast delay={doneIconDuration}>
                      <CheckCircle2 />
                    </InlineToast>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        ) : (
          <div className={cn('relative', iconMarginClassLeft)}>
            <div className="invisible" aria-hidden="true">
              {iconForRight}
            </div>
            {isLoadingButton && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 top-0"
              >
                <LoadingSpinner />
              </motion.div>
            )}
            {isErrorButton && !isLoadingButton && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 top-0"
              >
                <CircleAlert />
              </motion.div>
            )}
            {isSuccessButton && !isLoadingButton && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 top-0"
              >
                <InlineToast
                  delay={doneIconDuration}
                  contentAfter={
                    <motion.div
                      initial={{ opacity: 1 }}
                      animate={{
                        opacity: isLoadingButton || isErrorButton ? 0 : 1
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      {iconForRight}
                    </motion.div>
                  }
                >
                  <CheckCircle2 />
                </InlineToast>
              </motion.div>
            )}
            {!isSuccessButton && (
              <motion.div
                initial={{ opacity: 1 }}
                animate={{
                  opacity:
                    isLoadingButton || isErrorButton || isSuccessButton ? 0 : 1
                }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 top-0"
              >
                {iconForRight}
              </motion.div>
            )}
          </div>
        )
      ) : (
        iconForRight && (
          <span className={iconMarginClassLeft}>{iconForRight}</span>
        )
      )}
    </Button>
  );
}
